/**
 * API Client — fetch wrapper dengan envelope types.
 *
 * Semua request ke Worker /api/* melewati fungsi ini.
 * Auth header (Bearer ID token) di-inject dari AuthContext.
 */

import type { ApiResponse, DeliveryType } from "@ag5r-store/shared";

export interface ApiClientConfig {
  baseUrl?: string;
  getIdToken?: () => Promise<string | null>;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface PaymentMethodOption {
  id: string;
  name: string;
  slug: string;
  fee_percentage: number;
  fee_fixed: number;
  logo?: string;
  description?: string;
}

/** Channel pembayaran Fansku (Bank Transfer, E-Wallet, QRIS, dst). */
export interface PaymentChannel {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
  payment_methods: PaymentMethodOption[];
}

/** Baris produk sebagaimana dikembalikan endpoint admin. */
export interface AdminProductRow {
  id: string;
  name: string;
  product_code: string;
  slug: string;
  delivery_type: DeliveryType;
  is_featured: number;
  /** 0 = disabled: hilang dari katalog publik, tetap tampil di admin. */
  is_active: number;
  category: string | null;
  created_at: string;
}

class ApiClient {
  private baseUrl: string;
  private getIdToken: () => Promise<string | null> = async () => null;

  constructor(config: ApiClientConfig = {}) {
    // Default: pakai relative path (Vite proxy ke Worker di dev).
    // Untuk production, set VITE_API_BASE_URL=https://worker.example.workers.dev
    this.baseUrl = config.baseUrl ?? import.meta.env.VITE_API_BASE_URL ?? "";
  }

  setTokenProvider(fn: () => Promise<string | null>) {
    this.getIdToken = fn;
  }

  private async request<T>(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    path: string,
    body?: unknown,
    options?: { auth?: boolean },
  ): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (options?.auth !== false) {
      const token = await this.getIdToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
    }

    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    let data: ApiResponse<T> | null = null;
    try {
      data = await res.json();
    } catch {
      throw new ApiError("PARSE_ERROR", "Invalid JSON response", res.status);
    }

    if (!data) {
      throw new ApiError("EMPTY_RESPONSE", "Empty response", res.status);
    }

    if (!data.ok) {
      throw new ApiError(
        data.error.code,
        data.error.message,
        res.status,
        data.error.details,
      );
    }

    return data.data;
  }

  // ===== Typed helpers =====

  get<T>(path: string, options?: { auth?: boolean }) {
    return this.request<T>("GET", path, undefined, options);
  }

  post<T>(path: string, body?: unknown, options?: { auth?: boolean }) {
    return this.request<T>("POST", path, body, options);
  }

  put<T>(path: string, body?: unknown, options?: { auth?: boolean }) {
    return this.request<T>("PUT", path, body, options);
  }

  patch<T>(path: string, body?: unknown, options?: { auth?: boolean }) {
    return this.request<T>("PATCH", path, body, options);
  }

  // `delete` adalah reserved word untuk nama method, jadi dipendekkan.
  del<T>(path: string, options?: { auth?: boolean }) {
    return this.request<T>("DELETE", path, undefined, options);
  }

  // ===== Domain endpoints =====

  // Public
  health() {
    return this.get<{ status: string; timestamp: string; service: string }>(
      "/api/health",
      { auth: false },
    );
  }

  listProducts(params?: { q?: string; category?: string; featured?: boolean }) {
    const sp = new URLSearchParams();
    if (params?.q) sp.set("q", params.q);
    if (params?.category) sp.set("category", params.category);
    if (params?.featured) sp.set("featured", "true");
    const qs = sp.toString();
    return this.get<import("@ag5r-store/shared").ProductListItem[]>(
      `/api/products${qs ? `?${qs}` : ""}`,
      { auth: false },
    );
  }

  getProduct(slug: string) {
    return this.get<import("@ag5r-store/shared").ProductDetail>(
      `/api/products/${encodeURIComponent(slug)}`,
      { auth: false },
    );
  }

  /** Dipanggil berkala halaman pembayaran sampai webhook mengubah status. */
  getOrderStatus(orderId: string) {
    return this.get<{
      order_id: string;
      status: "PENDING" | "PAID" | "FAILED" | "EXPIRED";
      amount: number;
      expires_at: string | null;
    }>(`/api/orders/${orderId}/status`);
  }

  /** Batalkan pesanan PENDING milik sendiri. */
  cancelOrder(orderId: string) {
    return this.post<{ order_id: string; status: string }>(
      `/api/orders/${orderId}/cancel`,
    );
  }

  getProfile() {
    return this.get<{
      uid: string;
      email: string;
      role: "owner" | "user";
      display_name: string | null;
      created_at: string;
    }>("/api/user/profile");
  }

  /** Kirim string kosong untuk mengembalikan nama ke default. */
  updateProfile(displayName: string) {
    return this.patch<{ display_name: string | null }>("/api/user/profile", {
      display_name: displayName,
    });
  }

  // Auth
  syncUser() {
    return this.post<{ uid: string; email: string; role: "owner" | "user"; created_at: string }>(
      "/api/auth/sync",
    );
  }

  // User
  createOrder(req: import("@ag5r-store/shared").CreateOrderRequest) {
    return this.post<import("@ag5r-store/shared").CreateOrderResponse>(
      "/api/orders/create",
      req,
    );
  }

  getVault() {
    return this.get<import("@ag5r-store/shared").UserVaultResponse>(
      "/api/user/vault",
    );
  }

  // Admin
  getAdminStats() {
    return this.get<{
      total_products: number;
      total_variants: number;
      total_users: number;
      total_orders: number;
      total_paid: number;
      total_revenue: number;
      /** Produk terarsip — tidak ikut dihitung di total_products. */
      archived_products: number;
    }>("/api/admin/stats");
  }

  // ===== Pembayaran =====

  /** Publik — pembeli memilih metode sebelum pesanan dibuat. */
  getPaymentMethods() {
    return this.get<PaymentChannel[]>("/api/payment-methods", { auth: false });
  }

  // ===== Landing / settings =====

  /** Publik — tanpa auth. */
  getLanding() {
    return this.get<import("@ag5r-store/shared").LandingContent>(
      "/api/settings",
      { auth: false },
    );
  }

  getSettingsAdmin() {
    return this.get<import("@ag5r-store/shared").SiteSettings>("/api/admin/settings");
  }

  updateSettings(req: import("@ag5r-store/shared").SiteSettings) {
    return this.put<{ updated: boolean }>("/api/admin/settings", req);
  }

  createProduct(req: import("@ag5r-store/shared").CreateProductRequest) {
    return this.post<{ id: string; slug: string; product_code: string }>(
      "/api/admin/products",
      req,
    );
  }

  listProductsAdmin() {
    return this.get<AdminProductRow[]>("/api/admin/products");
  }

  /** Isi & riwayat pemakaian stok satu varian. Payload sudah dimasking server. */
  listPool(variantId: string) {
    return this.get<Array<{
      id: string;
      secret_preview: string;
      is_claimed: boolean;
      claimed_by: string | null;
      claimed_at: string | null;
      claimed_order_id: string | null;
      created_at: string;
    }>>(`/api/admin/pool/list/${encodeURIComponent(variantId)}`);
  }

  listArchivedProducts() {
    return this.get<AdminProductRow[]>("/api/admin/products/archived");
  }

  restoreProduct(id: string) {
    return this.post<{ id: string; restored: boolean }>(
      `/api/admin/products/${id}/restore`,
    );
  }

  /** Angka uang menurut Fansku — beda dari hitungan lokal, lihat komentar API. */
  getEarnings() {
    return this.get<
      | {
          available: true;
          total_supports: number;
          total_amount: number;
          total_creator_earning: number;
          available_balance: number;
          pending_balance: number;
        }
      | { available: false; error: string }
    >("/api/admin/earnings");
  }

  getProductAdmin(id: string) {
    return this.get<AdminProductRow & {
      description: string | null;
      images: string;
      download_url: string | null;
      variants: Array<{
        id: string;
        name: string;
        price: number;
        duration_days: number;
        stock_available: number;
      }>;
    }>(`/api/admin/products/${id}`);
  }

  updateProduct(id: string, req: import("@ag5r-store/shared").CreateProductRequest) {
    return this.put<{ id: string; slug: string; product_code: string }>(
      `/api/admin/products/${id}`,
      req,
    );
  }

  setProductActive(id: string, active: boolean) {
    return this.patch<{ id: string; is_active: boolean }>(
      `/api/admin/products/${id}/active`,
      { active },
    );
  }

  /**
   * Server memutuskan sendiri: produk yang belum pernah dipesan dihapus
   * permanen, yang sudah terjual diarsip agar lisensi pembeli tidak putus.
   * `mode` memberitahu mana yang terjadi.
   */
  deleteProduct(id: string) {
    return this.del<{ id: string; mode: "archived" | "deleted"; order_count: number }>(
      `/api/admin/products/${id}`,
    );
  }

  createVariant(req: import("@ag5r-store/shared").CreateVariantRequest) {
    return this.post<{ variant_id: string }>("/api/admin/variants", req);
  }

  listVariantsAdmin(productId: string) {
    return this.get<Array<{
      id: string;
      product_id: string;
      name: string;
      duration_days: number;
      price: number;
      is_active: number;
    }>>(`/api/admin/variants/${encodeURIComponent(productId)}`);
  }

  updateVariant(id: string, req: { name: string; duration_days: number; price: number }) {
    return this.put<{ id: string }>(`/api/admin/variants/${id}`, req);
  }

  setVariantActive(id: string, active: boolean) {
    return this.patch<{ id: string; is_active: boolean }>(
      `/api/admin/variants/${id}/active`,
      { active },
    );
  }

  /**
   * Server memutuskan: varian yang belum pernah dipesan dihapus permanen,
   * yang sudah punya riwayat hanya dinonaktifkan agar pesanan lama utuh.
   */
  deleteVariant(id: string) {
    return this.del<{ id: string; mode: "deleted" | "disabled"; order_count: number }>(
      `/api/admin/variants/${id}`,
    );
  }

  bulkInsertPool(req: import("@ag5r-store/shared").BulkPoolInsertRequest) {
    return this.post<{ inserted: number; variant_id: string }>(
      "/api/admin/pool/bulk",
      req,
    );
  }

  getPoolStats(variantId: string) {
    return this.get<{ variant_id: string; stock_available: number }>(
      `/api/admin/pool/stats/${encodeURIComponent(variantId)}`,
    );
  }

  getCloudinarySign(folder?: string) {
    const qs = folder ? `?folder=${encodeURIComponent(folder)}` : "";
    return this.get<import("@ag5r-store/shared").CloudinarySignResponse>(
      `/api/admin/cloudinary/sign${qs}`,
    );
  }

  listOrdersAdmin(limit = 100) {
    return this.get<Array<{
      id: string;
      user_id: string;
      variant_id: string;
      amount: number;
      gateway_trx_id: string | null;
      status: string;
      webhook_processed_at: string | null;
      expires_at: string | null;
      created_at: string;
    }>>(`/api/admin/orders?limit=${limit}`);
  }
}

export const api = new ApiClient();